import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { resolveImageUrl } from "../../services/mediaUrl";

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;

function unique(values) {
  return [...new Set((values || []).filter(Boolean).map(String))];
}

function getActorId(row) {
  const direct =
    row?.actor_id ||
    row?.sender_id ||
    row?.from_user_id ||
    row?.source_user_id ||
    row?.triggered_by ||
    row?.created_by ||
    row?.reactor_id ||
    row?.commenter_id ||
    row?.metadata?.actor_id ||
    row?.metadata?.sender_id ||
    row?.metadata?.from_user_id ||
    null;

  if (direct) return String(direct);

  // Legacy notifications sometimes stored the actor UUID inside the message.
  const match = String(row?.message || "").match(UUID_RE);
  return match?.[0] || null;
}

function getTargetId(row) {
  return row?.target_id ?? row?.user_item_id ?? row?.metadata?.user_item_id ?? null;
}

function getCommentId(row) {
  return row?.comment_id ?? row?.metadata?.comment_id ?? null;
}

function normalizeDbId(value) {
  if (value === null || value === undefined || value === "") return value;
  const text = String(value);
  return /^\d+$/.test(text) ? Number(text) : text;
}

export default function useNotifications(currentUser) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshNotifications = useCallback(async () => {
    if (!currentUser?.id) {
      setNotifications([]);
      return [];
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", currentUser.id)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;

      const rows = data || [];

      // ----------------------------------------------------------
      // ACTORS
      // ----------------------------------------------------------
      const actorIds = unique(rows.map(getActorId));
      let actorsById = {};

      if (actorIds.length) {
        const { data: actors, error: actorsError } = await supabase.rpc(
          "get_user_profiles",
          {
            p_user_ids: actorIds,
          }
        );

        if (actorsError) {
          console.warn("Could not enrich notification actors:", actorsError);
        } else {
          actorsById = Object.fromEntries(
            (actors || []).map((actor) => [String(actor.id), actor])
          );
        }
      }

      // ----------------------------------------------------------
      // PUBLIC COLLECTION ITEM TARGETS
      // ----------------------------------------------------------
      const targetIds = unique(
        rows
          .filter((row) => {
            const type = String(row?.target_type || "").toLowerCase();
            const notificationType = String(
              row?.type || row?.notification_type || ""
            ).toLowerCase();

            return (
              type === "user_item" ||
              row?.user_item_id != null
            );
          })
          .map(getTargetId)
      );

      let userItemsById = {};
      let canonicalById = {};
      let primaryImagesByUserItemId = {};

      if (targetIds.length) {
        const dbTargetIds = targetIds.map(normalizeDbId);

        const [userItemResult, imageResult] = await Promise.all([
          supabase
            .from("user_items")
            .select(
              "id, item_id, user_id, user_specific_name, user_specific_brand, user_specific_set_name, user_specific_year"
            )
            .in("id", dbTargetIds),
          supabase
            .from("item_images")
            .select("id, user_item_id, position, created_at, storage_provider, storage_bucket, object_key")
            .in("user_item_id", dbTargetIds)
            .order("position", { ascending: true }),
        ]);

        if (userItemResult.error) {
          console.warn("Could not enrich notification items:", userItemResult.error);
        } else {
          userItemsById = Object.fromEntries(
            (userItemResult.data || []).map((item) => [String(item.id), item])
          );
        }

        if (imageResult.error) {
          console.warn("Could not enrich notification images:", imageResult.error);
        } else {
          for (const image of imageResult.data || []) {
            const key = String(image.user_item_id);
            if (!primaryImagesByUserItemId[key]) {
              primaryImagesByUserItemId[key] = image;
            }
          }
        }

        const canonicalIds = unique(
          Object.values(userItemsById).map((item) => item.item_id)
        ).map(normalizeDbId);

        if (canonicalIds.length) {
          const { data: canonicalItems, error: canonicalError } = await supabase
            .from("items")
            .select("id, name, brand, set_name, year, stock_image_storage_provider, stock_image_storage_bucket, stock_image_object_key")
            .in("id", canonicalIds);

          if (canonicalError) {
            console.warn(
              "Could not enrich notification canonical items:",
              canonicalError
            );
          } else {
            canonicalById = Object.fromEntries(
              (canonicalItems || []).map((item) => [String(item.id), item])
            );
          }
        }
      }

      // ----------------------------------------------------------
      // COMMENT PREVIEWS
      // ----------------------------------------------------------
      const commentIds = unique(rows.map(getCommentId));
      let commentsById = {};

      if (commentIds.length) {
        const { data: comments, error: commentsError } = await supabase
          .from("social_comments")
          .select("id, comment, user_id, target_type, target_id, parent_comment_id")
          .in("id", commentIds);

        if (commentsError) {
          console.warn("Could not enrich notification comments:", commentsError);
        } else {
          commentsById = Object.fromEntries(
            (comments || []).map((comment) => [String(comment.id), comment])
          );
        }
      }

      // ----------------------------------------------------------
      // FINAL ENRICHED NOTIFICATIONS
      // ----------------------------------------------------------
      const enriched = rows.map((row) => {
        const actorId = getActorId(row);
        const targetId = getTargetId(row);
        const commentId = getCommentId(row);

        const userItem = targetId
          ? userItemsById[String(targetId)] || null
          : null;

        const canonical = userItem?.item_id
          ? canonicalById[String(userItem.item_id)] || null
          : null;

        const primaryImage = targetId
          ? primaryImagesByUserItemId[String(targetId)] || null
          : null;

        const target = userItem
          ? {
              id: userItem.id,
              user_id: userItem.user_id,
              name:
                userItem.user_specific_name ||
                canonical?.name ||
                "Collection item",
              brand:
                userItem.user_specific_brand ||
                canonical?.brand ||
                "",
              set_name:
                userItem.user_specific_set_name ||
                canonical?.set_name ||
                "",
              year:
                userItem.user_specific_year ||
                canonical?.year ||
                "",
              image_url:
                resolveImageUrl(primaryImage) ||
                resolveImageUrl(
                  canonical?.stock_image_object_key
                    ? {
                        storage_provider: canonical.stock_image_storage_provider,
                        storage_bucket: canonical.stock_image_storage_bucket,
                        object_key: canonical.stock_image_object_key,
                      }
                    : null
                ) ||
                null,
            }
          : null;

        return {
          ...row,
          actor_id: row.actor_id || actorId || null,
          actor:
            (actorId && actorsById[String(actorId)]) ||
            row.actor ||
            null,
          target,
          comment: commentId
            ? commentsById[String(commentId)] || null
            : null,
        };
      });

      setNotifications(enriched);
      return enriched;
    } catch (err) {
      console.error("Failed to load notifications:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const markAsRead = useCallback(
    async (notificationId) => {
      if (!notificationId) return;

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) {
        console.error("Failed to mark notification as read:", error);
        refreshNotifications();
      }
    },
    [refreshNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    if (!currentUser?.id) return;

    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, is_read: true }))
    );

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", currentUser.id)
      .eq("is_read", false);

    if (error) {
      console.error("Failed to mark notifications as read:", error);
      refreshNotifications();
    }
  }, [currentUser?.id, refreshNotifications]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    const channel = supabase
      .channel(`notifications-${currentUser.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${currentUser.id}`,
        },
        () => refreshNotifications()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id, refreshNotifications]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications]
  );

  return {
    notifications,
    unreadCount,
    loading,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
  };
}
